"use client"

import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { updateFaculty, fetchFaculty } from "../store/slices/facultySlice"
import { fetchDepartments } from "../store/slices/departmentSlice"
import { X, Save } from "lucide-react" // Added Save icon

const EditFaculty = ({ faculty, setEditingFaculty }) => {
  const dispatch = useDispatch()
  const { departments } = useSelector((state) => state.departments)
  const [formData, setFormData] = useState({
    name: faculty.name || "",
    email: faculty.email || "",
    employee_id: faculty.employee_id || "",
    faculty_code: faculty.faculty_code || "",
    department_id: faculty.department_id || "",
    designation: faculty.designation || "",
    phone: faculty.phone || faculty.mobile_no || "",
    qualification: faculty.qualification || "",
    experience_years: String(faculty.experience_years || ""),
    joining_date: faculty.joining_date ? faculty.joining_date.split("T")[0] : "",
  })
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    dispatch(fetchDepartments())
  }, [dispatch])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    // Clean data before submission:
    // 1. Convert experience_years to a number (if possible)
    const dataToSend = {
      ...formData,
      experience_years: Number(formData.experience_years),
    }

    try {
      // The faculty ID is correctly passed as part of the action payload.
      await dispatch(updateFaculty({ id: faculty.faculty_id, data: dataToSend })).unwrap()
      dispatch(fetchFaculty()) // Refresh faculty list
      setEditingFaculty(null) // Close the form
    } catch (err) {
      // Improved error message extraction
      const errorMessage =
        err.message || err.error?.message || "Failed to update faculty due to a network or server error."
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Common input styling for consistency
  const inputClass =
    "w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-150 ease-in-out"

  return (
    <div className="relative bg-white dark:bg-gray-800 p-8 rounded-xl shadow-2xl max-w-4xl mx-auto border border-gray-100 dark:border-gray-700">
      {/* Header and Close Button */}
      <div className="flex justify-between items-center mb-6 border-b pb-4 border-gray-200 dark:border-gray-700">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Faculty Details</h3>
        <button
          onClick={() => setEditingFaculty(null)}
          className="p-2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 rounded-lg font-medium">
          Error: {error}
        </div>
      )}

      {/* Form Grid */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          {/* Form Fields */}
          {[
            { label: "Name", name: "name", type: "text", required: true, placeholder: "Enter full name" },
            { label: "Email", name: "email", type: "email", required: true, placeholder: "Enter email address" },
            {
              label: "Employee ID",
              name: "employee_id",
              type: "text",
              required: true,
              placeholder: "Enter employee ID",
            },
            {
              label: "Faculty Code",
              name: "faculty_code",
              type: "text",
              required: false,
              placeholder: "Enter faculty code",
            },
            {
              label: "Designation",
              name: "designation",
              type: "text",
              required: true,
              placeholder: "Enter designation",
            },
            { label: "Phone", name: "phone", type: "text", required: true, placeholder: "Enter phone number" },
            {
              label: "Qualification",
              name: "qualification",
              type: "text",
              required: true,
              placeholder: "Enter qualification",
            },
            {
              label: "Experience (Years)",
              name: "experience_years",
              type: "number",
              required: true,
              placeholder: "Enter years of experience",
            },
            { label: "Joining Date", name: "joining_date", type: "date", required: true, placeholder: "" },
          ].map((field) => (
            <div key={field.name}>
              <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <input
                id={field.name}
                type={field.type}
                name={field.name}
                value={formData[field.name]}
                onChange={handleChange}
                required={field.required}
                className={inputClass}
                placeholder={field.placeholder}
              />
            </div>
          ))}

          {/* Department Dropdown */}
          <div>
            <label htmlFor="department_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {"Institute / Department"}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <select
              id="department_id"
              name="department_id"
              className={inputClass}
              value={formData.department_id}
              onChange={handleChange}
              required
            >
              <option value="" disabled>
                Select department
              </option>
              {(departments || []).map((d) => (
                <option key={d.department_id} value={d.department_id}>
                  {`${d.code} — ${d.name}`}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={() => setEditingFaculty(null)}
            className="px-6 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium rounded-lg transition-colors bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-md transition-all duration-200 ${
              loading ? "opacity-60 cursor-not-allowed" : "shadow-blue-500/30 hover:shadow-lg"
            }`}
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>Updating...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Update Faculty</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default EditFaculty
